import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import GoalSummaryCard from "@src/components/cards/operator/GoalSummaryCard";
import { SignolUtils, useIsMobileWidth, useIsTabletWidth } from "@src/utils";
import { useDCContext } from "@src/context";
import { useOperatorContext } from "@src/context/operatorContext";
import OperatorHooks from "@src/context/hooks/operatorHooks";
import {
  DialogBubble,
  Icon,
  Loader,
  PeriodDropDown,
  Tooltip,
} from "@src/components/atoms";
import { checkShouldCustomiseGoal } from "@src/components/cards/cardHelpers";
import { OperatorBehaviourCarousel } from "@src/components/atoms/CarouselSignol";
import { getBehaviourColourForPosition } from "@signolteam/shared/colours";

const GOAL_PROGRESS_CELEBRATION_KEY = "goal_progress_celebration_shown";
const GOAL_PROGRESS_CELEBRATION_DELAY_MS = 2000;

/**
 * @param {{
 * dataDelayMessage: string,
 * }} props
 */
const OverviewGoals = ({ dataDelayMessage }) => {
  const {
    profile,
    setProfile,
    selectedPeriod,
    setSelectedPeriod,
    setSelectedBehaviour,
    periodName,
    onboardingOpen,
  } = useOperatorContext();
  const dc = useDCContext();
  const navigate = useNavigate();
  const { customer } = profile;
  const industry = customer.industry;
  const isMobile = useIsMobileWidth();
  const isLargeMobile = useIsTabletWidth();

  const {
    data: { goalsData, currentPeriod } = {},
    isPending: goalsPending,
    error: goalsError,
  } = OperatorHooks.useOperatorGoals(profile?.operator?.id);

  const currentPeriodBehaviours = goalsData?.find(
    (g) => g.groupKey === currentPeriod?.groupKey,
  )?.behaviours;

  const [celebrationBehaviourAcronym, setCelebrationBehaviourAcronym] =
    useState(null);
  const celebrationTriggeredRef = useRef(false);
  const celebrationMutation = OperatorHooks.useSubmitOperatorProfile(
    (profileData) => profileData && setProfile(profileData),
  );

  useEffect(() => {
    if (!!currentPeriod && !selectedPeriod) {
      setSelectedPeriod(currentPeriod);
    }
  }, [currentPeriod]);

  useEffect(() => {
    if (celebrationTriggeredRef.current) return;
    if (goalsPending || !currentPeriod || !currentPeriodBehaviours) return;
    if (onboardingOpen) return; // wait until onboarding is complete before celebrating

    celebrationTriggeredRef.current = true;

    const alreadyShown = profile.operatorMetadata?.some(
      (m) => m.key === GOAL_PROGRESS_CELEBRATION_KEY,
    );
    if (alreadyShown) return;

    const celebrationBehaviour = currentPeriodBehaviours
      .filter(
        (b) =>
          b.goalTargetEvents > 0 &&
          Math.max(b.succeeded, b.succeededIncludingSelfReported) > 0,
      )
      .sort((a, b) => {
        const progressA =
          Math.max(a.succeeded, a.succeededIncludingSelfReported) /
          a.goalTargetEvents;
        const progressB =
          Math.max(b.succeeded, b.succeededIncludingSelfReported) /
          b.goalTargetEvents;
        return progressB - progressA || a.order - b.order;
      })[0];

    if (!celebrationBehaviour) return;

    setSelectedPeriod(currentPeriod);
    setSelectedBehaviour(celebrationBehaviour);
    setCelebrationBehaviourAcronym(celebrationBehaviour.behaviourAcronym);
    celebrationMutation.mutateAsync({
      metadata: [
        {
          key: GOAL_PROGRESS_CELEBRATION_KEY,
          type: "STRING",
          value: "true",
        },
      ],
    });
  }, [goalsPending, currentPeriod, currentPeriodBehaviours, onboardingOpen]);

  const navigateToGoals = (bvr) => {
    setSelectedBehaviour(bvr);
    navigate("/dashboard/operator/goals");
  };

  const renderGoalSummaryCard = (behaviour, card) =>
    behaviour.behaviourAcronym === celebrationBehaviourAcronym ? (
      <DialogBubble
        target={card}
        display="block"
        direction="down"
        dismissOnOutsideClick
        overlayColor={getBehaviourColourForPosition(behaviour.order)?.name}
        revealDelayMs={GOAL_PROGRESS_CELEBRATION_DELAY_MS}
        onDismiss={() => setCelebrationBehaviourAcronym(null)}
      >
        {dc.getDynamicReplacement(
          "captain__overview__goal_progress_celebration",
          { BEHAVIOUR_NAME: behaviour.behaviourAcronym },
          `Nice work. You've already made progress towards your ${behaviour.behaviourAcronym} goal.`,
        )}
      </DialogBubble>
    ) : (
      card
    );

  return (
    <div className="mx-6 mt-9">
      <div className="mx-auto max-w-lg flex flex-col md:flex-row items-center justify-evenly">
        <div
          className={`text-2xl md:text-4xl font-extrabold text-center leading-snug capitalize ${isMobile ? "mb-2" : ""}`}
        >
          {dc.getDynamicReplacement("captain__overview__targets")}
        </div>
        {!!goalsData && goalsData.length > 0 && (
          <div className={"flex flex-row items-center justify-evenly gap-2"}>
            <PeriodDropDown goalsData={goalsData ? goalsData : []} />
            <Tooltip
              content={[
                dc.getDynamicReplacement("captain__tooltip__overview_targets"),
              ]}
              direction="down-left"
              absolute={false}
              centered={true}
            />
          </div>
        )}
      </div>
      <div className="text-base font-medium flex flex-col items-center my-4">
        <span>{dataDelayMessage}</span>
      </div>

      {goalsPending ? (
        <div className="min-h-120">
          <div className="w-full h-full flex items-center justify-center">
            <Loader size="2xl" />
          </div>
        </div>
      ) : goalsError ? (
        <div>Error fetching goal summaries...</div>
      ) : !!goalsData && goalsData.length > 0 ? (
        <div>
          <div className="mb-2">
            {!!isMobile || !!isLargeMobile ? (
              <div className="py-0">
                <OperatorBehaviourCarousel>
                  {goalsData
                    ?.find((g) =>
                      !!selectedPeriod
                        ? g.groupKey ==
                          (selectedPeriod?.value || selectedPeriod?.groupKey)
                        : g.groupKey == currentPeriod.groupKey,
                    )
                    ?.behaviours?.sort((a, b) => a?.order > b?.order)
                    ?.map((behaviour, idx) => (
                      <div
                        className="px-4 h-full"
                        style={{ width: "243px" }}
                        key={`${!!selectedPeriod ? selectedPeriod?.value : currentPeriod?.groupKey}-${behaviour.behaviourAcronym}-${idx}`}
                      >
                        {renderGoalSummaryCard(
                          behaviour,
                          <GoalSummaryCard
                            behaviour={behaviour}
                            customiseGoalReason={checkShouldCustomiseGoal(
                              behaviour,
                              goalsData?.find(
                                (g) => g.groupKey === selectedPeriod?.groupKey,
                              )?.behaviours || [],
                            )}
                            getDynamicContent={dc.getDynamicReplacement}
                            handleClick={navigateToGoals}
                            industry={industry}
                            periodName={periodName}
                            position={behaviour.order}
                            preferredEventName={customer.preferredEventName}
                            preferredEventNamePlural={
                              customer.preferredEventNamePlural
                            }
                            targetActual={Math.max(
                              behaviour.succeeded,
                              behaviour.succeededIncludingSelfReported,
                            )}
                            targetGoal={behaviour.goalTargetEvents}
                            isOpen={behaviour?.isOpenPeriod}
                            isCurrent={
                              selectedPeriod?.value ===
                                currentPeriod.groupKey ||
                              selectedPeriod?.groupKey ===
                                currentPeriod.groupKey
                            }
                            trackingActionKey="OVERVIEW_BEHAVIOUR_TARGET_SELECTION"
                          />,
                        )}
                      </div>
                    ))}
                </OperatorBehaviourCarousel>
              </div>
            ) : (
              <div className="mx-4 flex justify-center overflow-x-auto">
                <div className="flex max-w-full">
                  {goalsData
                    ?.find((g) =>
                      !!selectedPeriod
                        ? g.groupKey ==
                          (selectedPeriod?.value || selectedPeriod?.groupKey)
                        : g.groupKey == currentPeriod.groupKey,
                    )
                    ?.behaviours?.sort((a, b) => a?.order > b?.order)
                    ?.map((behaviour, idx) => {
                      return (
                        <div
                          className="p-4"
                          style={{ width: "243px" }}
                          key={`${!!selectedPeriod ? selectedPeriod.value : currentPeriod.groupKey}-${behaviour.behaviourAcronym}-${idx}`}
                        >
                          {renderGoalSummaryCard(
                            behaviour,
                            <GoalSummaryCard
                              behaviour={behaviour}
                              customiseGoalReason={checkShouldCustomiseGoal(
                                behaviour,
                                goalsData?.find(
                                  (g) =>
                                    g.groupKey === selectedPeriod?.groupKey,
                                )?.behaviours || [],
                              )}
                              getDynamicContent={dc.getDynamicReplacement}
                              handleClick={navigateToGoals}
                              industry={industry}
                              periodName={periodName}
                              position={behaviour.order}
                              preferredEventName={customer.preferredEventName}
                              preferredEventNamePlural={
                                customer.preferredEventNamePlural
                              }
                              targetActual={Math.max(
                                behaviour.succeeded,
                                behaviour.succeededIncludingSelfReported,
                              )}
                              targetGoal={behaviour.goalTargetEvents}
                              isOpen={behaviour?.isOpenPeriod}
                              isCurrent={
                                selectedPeriod?.value ===
                                  currentPeriod.groupKey ||
                                selectedPeriod?.groupKey ===
                                  currentPeriod.groupKey
                              }
                            />,
                          )}
                        </div>
                      );
                    })}
                </div>
              </div>
            )}
          </div>
        </div>
      ) : !!goalsData ? (
        <div className="flex gap-2 items-center justify-center text-left">
          <Icon name={"clockwise"} inline /> Signol is awaiting data from your
          company.
        </div>
      ) : null}
    </div>
  );
};

export default OverviewGoals;
